import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { setFeedbackBridge } from '../ui/feedback';

/*
 * Global feedback state: one queue of message dialogs (confirm / alert / prompt) and a toast stack.
 * The API is unchanged for existing callers (showConfirm, showAlert, showPrompt, showToast),
 * and every call now also returns a promise:
 *   showConfirm(...)  -> Promise<boolean>
 *   showAlert(...)    -> Promise<void>
 *   showPrompt(...)   -> Promise<string | null>
 * Dialogs are queued: a second request waits instead of replacing the one on screen.
 */

const ModalContext = createContext();

export function useModal() {
    return useContext(ModalContext);
}

let seq = 0;
const nextId = () => {
    seq += 1;
    return `${Date.now()}-${seq}`;
};

const MAX_TOASTS = 5;
const LOG_SIZE = 30;

export function ModalProvider({ children }) {
    const [queue, setQueue] = useState([]);
    const [toasts, setToasts] = useState([]);
    const [log, setLog] = useState([]);
    const timers = useRef({});

    const current = queue[0] || null;
    const queueRef = useRef(queue);
    queueRef.current = queue;
    const settledIds = useRef(new Set());

    const enqueue = useCallback((type, props) => new Promise((resolve) => {
        setQueue((q) => [...q, { id: nextId(), type, props, resolve }]);
    }), []);

    /** Close the dialog on screen. `result` is what the caller's promise resolves to. */
    const settle = useCallback((result) => {
        const head = queueRef.current[0];
        if (!head || settledIds.current.has(head.id)) return;
        settledIds.current.add(head.id);
        setQueue((q) => q.filter((item) => item.id !== head.id));
        try {
            if (head.type === 'confirm') {
                if (result) head.props.onConfirm && head.props.onConfirm();
                else head.props.onCancel && head.props.onCancel();
            } else if (head.type === 'alert') {
                head.props.onOk && head.props.onOk();
            } else if (head.type === 'prompt') {
                if (result !== null && result !== undefined) head.props.onSubmit && head.props.onSubmit(result);
                else head.props.onCancel && head.props.onCancel();
            }
        } finally {
            head.resolve(head.type === 'confirm' ? Boolean(result) : result);
        }
    }, []);

    // Legacy: hideModal() dismisses the current dialog as a cancel.
    const hideModal = useCallback(() => {
        settle(current && current.type === 'prompt' ? null : false);
    }, [settle, current]);

    const showConfirm = useCallback(({
        title = 'Are you sure?',
        message,
        startLabel,
        confirmLabel,
        cancelLabel = 'Cancel',
        onConfirm,
        onCancel,
        isDangerous = false,
        danger,
    } = {}) => enqueue('confirm', {
        title,
        message,
        confirmLabel: confirmLabel || startLabel || 'Confirm',
        cancelLabel,
        onConfirm,
        onCancel,
        isDangerous: Boolean(isDangerous || danger),
    }), [enqueue]);

    const showAlert = useCallback(({ title = 'Notice', message, buttonLabel = 'OK', onOk, isError = false, tone } = {}) => (
        enqueue('alert', { title, message, buttonLabel, onOk, isError: Boolean(isError || tone === 'error'), tone })
    ), [enqueue]);

    const showPrompt = useCallback(({
        title = 'Enter a value',
        message,
        label,
        placeholder,
        initialValue = '',
        confirmLabel = 'Submit',
        cancelLabel = 'Cancel',
        onSubmit,
        onCancel,
        inputType = 'text',
        required = false,
    } = {}) => enqueue('prompt', {
        title, message, label, placeholder, initialValue, confirmLabel, cancelLabel, onSubmit, onCancel, inputType, required,
    }), [enqueue]);

    const dismissToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        if (timers.current[id]) {
            clearTimeout(timers.current[id]);
            delete timers.current[id];
        }
    }, []);

    const showToast = useCallback((message, type = 'success', duration = 4000) => {
        const id = nextId();
        const kind = ['success', 'error', 'info', 'warning'].includes(type) ? type : 'info';
        const text = typeof message === 'string' ? message : String(message ?? '');
        setToasts((prev) => [...prev, { id, message: text, type: kind }].slice(-MAX_TOASTS));
        setLog((prev) => [{ id, message: text, type: kind, at: Date.now() }, ...prev].slice(0, LOG_SIZE));
        if (duration > 0) {
            timers.current[id] = setTimeout(() => dismissToast(id), duration);
        }
        return id;
    }, [dismissToast]);

    const clearLog = useCallback(() => setLog([]), []);

    // Imperative bridge for code outside React (see src/ui/feedback)
    useEffect(() => {
        setFeedbackBridge({
            toast: (message, type, duration) => showToast(message, type, duration),
            confirm: (o) => showConfirm({ ...o, isDangerous: o.danger || o.isDangerous }),
            prompt: (o) => showPrompt(o),
            alert: (o) => showAlert({ ...o, isError: o.isError || o.tone === 'error' }),
        });
        return () => setFeedbackBridge(null);
    }, [showToast, showConfirm, showPrompt, showAlert]);

    useEffect(() => () => {
        Object.values(timers.current).forEach(clearTimeout);
    }, []);

    const modal = useMemo(() => (
        current
            ? { isOpen: true, type: current.type, props: current.props, id: current.id }
            : { isOpen: false, type: null, props: {} }
    ), [current]);

    const value = {
        modal,
        settle,
        hideModal,
        showConfirm,
        showAlert,
        showPrompt,
        showToast,
        toasts,
        dismissToast,
        feedbackLog: log,
        clearFeedbackLog: clearLog,
    };

    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}
